// Parity-diff harness: during the shadow run (flag OFF, both engines execute),
// derive the legacy-shaped outputs from the projection and compare them to the
// REAL legacy outputs the legacy coverage calc just wrote. Logs per-field
// deltas with zero user impact, so we can measure divergence across real
// traffic and only flip the `claimProjectionEngine` flag once deltas are near
// zero per ported product.
//
// Gated to non-prod/local by the caller (same predicate as
// `shouldAssertProjectionIdentities`). Never throws — parity logging must not
// affect calc output.

import { Draft } from 'immer';
import {
  LegacyClaimantOutputs,
  claimantMonths,
  coverageKeyFor,
  deriveLegacyClaimantOutputs,
} from './projectionToLegacy';

// Relative tolerance below which a delta is treated as noise (floating-point /
// rounding). Paired with an absolute floor so tiny dollar amounts don't trip
// the relative check.
const REL_TOLERANCE = 0.01;
const ABS_TOLERANCE = 1;

// Per-phase fields worth diffing — the ones ROI and the user-facing surfaces
// actually consume. (We don't diff every SinglePhaseCalculations field; the
// noise would drown the signal.)
const PHASE_FIELDS: (keyof SinglePhaseCalculations)[] = [
  'phaseCostCoverage',
  'cumulativePolicyPayOut',
  'indemnityPayout',
  'remainingMaxBenefitAfterPhaseReduction',
  'inflatedDailyBenefitAmount',
  'inflatedMaxBenefitAmount',
  'maxPayoutAccordingToDailyMaximum',
  'inflatedDailyBenefitAmountBase',
  'inflatedDailyBenefitAmountRider',
  'epReimbursementAmount',
];

interface FieldDelta {
  field: string;
  legacy: number;
  derived: number;
  relDiff: number;
}

function pushDelta(field: string, legacy: number | null | undefined, derived: number | null | undefined, out: FieldDelta[]): void {
  const l = legacy ?? 0;
  const d = derived ?? 0;
  const absDiff = Math.abs(l - d);
  if (absDiff <= ABS_TOLERANCE) return;
  const denom = Math.max(Math.abs(l), Math.abs(d), 1);
  const relDiff = absDiff / denom;
  if (relDiff > REL_TOLERANCE) out.push({ field, legacy: l, derived: d, relDiff });
}

function diffClaimant(
  perspective: 'client' | 'partner',
  legacyPhaseCalcs: SinglePhaseCalculations[] | undefined,
  legacyCoverage: number | null | undefined,
  legacyPolicy: PolicyFundingSource,
  derived: LegacyClaimantOutputs,
  out: FieldDelta[],
): void {
  // Scalars. The partner perspective only carries coverage + remaining
  // (see `projectionToLegacy.applyClaimant` / legacy `calcPolicyCoverageImpl`);
  // legacy never sets the value/surplus scalars on the partner, so diffing them
  // against the pure-derived values would be a false delta.
  pushDelta(`${perspective}.coverage`, legacyCoverage, derived.scalars.coverage, out);
  pushDelta(`${perspective}.policyRemainingBenefitAmount`, legacyPolicy.policyRemainingBenefitAmount, derived.scalars.policyRemainingBenefitAmount, out);
  if (perspective === 'client') {
    pushDelta(`${perspective}.policyTotalValue`, legacyPolicy.policyTotalValue, derived.scalars.policyTotalValue, out);
    pushDelta(`${perspective}.policyNonLtcPayout`, legacyPolicy.policyNonLtcPayout, derived.scalars.policyNonLtcPayout, out);
    pushDelta(`${perspective}.indemnitySurplus`, legacyPolicy.indemnitySurplus, derived.scalars.indemnitySurplus, out);
  }

  // Per-phase fields (sparse arrays, indexed by care-phase index).
  const phaseCount = Math.max(legacyPhaseCalcs?.length ?? 0, derived.phaseCalculations.length);
  for (let i = 0; i < phaseCount; i += 1) {
    const legacyPhase = legacyPhaseCalcs?.[i];
    const derivedPhase = derived.phaseCalculations[i];
    if (!legacyPhase && !derivedPhase) continue;
    for (const field of PHASE_FIELDS) {
      pushDelta(
        `${perspective}.phase[${i}].${field}`,
        legacyPhase?.[field] as number | null | undefined,
        derivedPhase?.[field] as number | null | undefined,
        out,
      );
    }
  }
}

export interface ParityDiffContext {
  fundingSourceId: string;
  policyType: FundingPolicyType | null | undefined;
  isJoint: boolean;
  clientHasStartedLtc: boolean;
}

// Reads the REAL legacy outputs off the (post-coverage-calc) client + policy,
// derives adapter outputs from `projection`, and logs any per-field deltas.
export function logProjectionParityDiff(
  mutableClient: Draft<Client>,
  ltcPolicy: PolicyFundingSource,
  projection: ClaimProjection,
  ctx: ParityDiffContext,
): void {
  try {
    const coverageKey = coverageKeyFor(ctx.clientHasStartedLtc);

    const { clientMonths, partnerMonths } = claimantMonths(projection, ctx.isJoint);
    const out: FieldDelta[] = [];

    // Policy-wide scalars (value, non-LTC payout, surplus) are compared against
    // the REAL legacy outputs, which for joint policies aggregate both spouses.
    // Derive them from the full joint projection so the diff stays apples-to-
    // apples (mirrors `projectionToLegacy`'s joint session call).
    diffClaimant(
      'client',
      mutableClient.phaseCalculations,
      ltcPolicy[coverageKey],
      ltcPolicy,
      deriveLegacyClaimantOutputs(projection, clientMonths, projection.months),
      out,
    );

    const partner = mutableClient.mutableClientPartner;
    if (ctx.isJoint && partner) {
      diffClaimant(
        'partner',
        partner.phaseCalculations,
        partner.fundingSources.ltcPolicy[coverageKey],
        partner.fundingSources.ltcPolicy,
        deriveLegacyClaimantOutputs(projection, partnerMonths),
        out,
      );
    }

    if (out.length === 0) return;
  } catch (error) {
    console.error('[projection-parity] diff failed (non-fatal)', {
      fundingSourceId: ctx.fundingSourceId,
      error,
    });
  }
}
